import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { sendQuoteRequest, sendFallbackEmail, getWebhookStatus } from '../services/emailService';

const { FiHome, FiDollarSign, FiClock, FiShield, FiCheck, FiChevronDown, FiChevronUp, FiMail, FiPhone, FiMapPin, FiAlertCircle, FiCheckCircle, FiAlertTriangle, FiSettings, FiSend, FiMessageSquare } = FiIcons;

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    products: [],
    windowTypes: [],
    windowCount: '',
    doorTypes: [],
    conservatoryNeed: '',
    conservatoryStyle: '',
    propertyType: '',
    timeline: '',
    materials: [],
    glazingType: [],
    energyEfficiency: '',
    color: '',
    budget: '',
    fullName: '',
    postcode: '',
    phone: '',
    email: '',
    message: '',
    consent: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitMethod, setSubmitMethod] = useState('');

  // Check webhook configuration status
  const webhookStatus = getWebhookStatus();

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'email' && submitError) {
      setSubmitError('');
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.products.length > 0;
      case 2:
        if (formData.products.includes('Windows')) {
          return formData.windowTypes.length > 0 && formData.windowCount;
        }
        if (formData.products.includes('Doors')) {
          return formData.doorTypes.length > 0;
        }
        if (formData.products.includes('Conservatory')) {
          return formData.conservatoryNeed && formData.conservatoryStyle;
        }
        return true;
      case 3:
        return formData.propertyType && formData.timeline;
      case 5:
        return formData.fullName && formData.postcode && formData.phone && formData.email && formData.consent;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);
    setSubmitMethod('');

    try {
      console.log('🚀 Submitting quote request...');
      const result = await sendQuoteRequest(formData);

      if (result.success) {
        console.log('✅ Quote request processed successfully!');
        setSubmitSuccess(true);
        setSubmitMethod(result.method);
        setSubmitted(true);
      } else {
        throw new Error(result.message || 'Failed to send quote request');
      }
    } catch (error) {
      console.error('❌ Form submission error:', error);
      setSubmitError(`${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualEmail = () => {
    console.log('📧 User requested manual email option');
    sendFallbackEmail(formData);
    setSubmitted(true);
    setSubmitSuccess(false);
    setSubmitMethod('manual');
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon 
              icon={submitSuccess ? FiCheckCircle : FiMail} 
              className="text-3xl text-green-600" 
            />
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {submitSuccess 
              ? '✅ Quote Request Sent Successfully!' 
              : submitMethod === 'manual' 
                ? '📧 Email Client Opened!' 
                : '📧 Quote Request Prepared!'
            }
          </h2>

          <p className="text-gray-600 mb-6">
            {submitSuccess 
              ? 'Your quote request has been successfully sent to our system via webhook. We will contact you within 24 hours.'
              : submitMethod === 'manual'
                ? 'Your email client has been opened with all the quote details pre-filled. Please send the email to complete your request.'
                : 'Your quote request has been prepared. We will contact you within 24 hours after we receive it.'
            }
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-600 mb-2">
              <strong>Customer:</strong> {formData.fullName}
            </p>
            <p className="text-xs text-gray-600 mb-2">
              <strong>Email:</strong> {formData.email}
            </p>
            <p className="text-xs text-gray-600">
              <strong>Products:</strong> {formData.products.join(', ')} • <strong>Timeline:</strong> {formData.timeline}
            </p>
          </div>

          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>✅ Next Steps:</strong> We'll review your request and contact you within 24 hours with up to 3 local installer quotes.
              </p>
            </div>

            {!submitSuccess && submitMethod === 'fallback' && (
              <button
                onClick={handleManualEmail}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold mb-3"
              >
                📧 Open Email Client Again
              </button>
            )}

            <button
              onClick={() => {
                setSubmitted(false);
                setCurrentStep(1);
                setSubmitSuccess(false);
                setSubmitError('');
                setSubmitMethod('');
                setFormData({
                  products: [],
                  windowTypes: [],
                  windowCount: '',
                  doorTypes: [],
                  conservatoryNeed: '',
                  conservatoryStyle: '',
                  propertyType: '',
                  timeline: '',
                  materials: [],
                  glazingType: [],
                  energyEfficiency: '',
                  color: '',
                  budget: '',
                  fullName: '',
                  postcode: '',
                  phone: '',
                  email: '',
                  message: '',
                  consent: false
                });
              }}
              className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              🔄 Create Another Quote Request
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Get Your Free Double Glazing Quote
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Compare quotes from up to 3 local installers
          </p>
          <div className="bg-white rounded-lg shadow-lg p-4 inline-block">
            <p className="text-sm font-semibold text-blue-800">
              ✔ 100% Free • No Obligation • Local Installers • Instant Delivery
            </p>
          </div>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Step {currentStep} of 5</span>
            <span className="text-sm font-medium text-gray-600">{Math.round((currentStep / 5) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-blue-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / 5) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Error Message with Manual Option */}
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-start">
              <SafeIcon icon={FiAlertTriangle} className="text-red-500 mr-3 mt-1" />
              <div className="flex-1">
                <p className="text-red-800 font-semibold mb-3">
                  Webhook Error: {submitError}
                </p>
                <p className="text-red-700 text-sm mb-4">
                  The webhook system is temporarily unavailable. You can still send your quote request manually using the button below.
                </p>
                <button
                  onClick={handleManualEmail}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center"
                >
                  <SafeIcon icon={FiMail} className="mr-2" />
                  📧 Send Quote Request via Email
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <motion.div
          className="bg-white rounded-2xl shadow-2xl p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <form onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              {/* Step 1: Product Selection */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    What type of product(s) do you need quotes for?
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { value: 'Windows', icon: FiHome, desc: 'All types of windows' },
                      { value: 'Doors', icon: FiHome, desc: 'Entry and patio doors' },
                      { value: 'Conservatory', icon: FiHome, desc: 'Extensions and conservatories' },
                      { value: 'Not Sure', icon: FiHome, desc: 'Get help choosing' }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          formData.products.includes(option.value)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.products.includes(option.value)}
                          onChange={() => handleCheckboxChange('products', option.value)}
                          className="sr-only"
                        />
                        <SafeIcon icon={option.icon} className="text-2xl text-blue-600 mr-4" />
                        <div>
                          <div className="font-semibold text-gray-800">{option.value}</div>
                          <div className="text-sm text-gray-600">{option.desc}</div>
                        </div>
                        {formData.products.includes(option.value) && (
                          <SafeIcon icon={FiCheck} className="text-blue-600 ml-auto" />
                        )}
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Product-specific Questions */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  {/* Windows Questions */}
                  {formData.products.includes('Windows') && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-bold text-gray-800">Windows</h3>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-4">What type of windows?</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {['Casement', 'Sliding Sash', 'Tilt & Turn', 'Bay', 'Bow', 'Fixed / Picture', 'Roof Window / Skylight', 'French Windows', 'Not Sure'].map((type) => (
                            <label
                              key={type}
                              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                                formData.windowTypes.includes(type)
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.windowTypes.includes(type)}
                                onChange={() => handleCheckboxChange('windowTypes', type)}
                                className="sr-only"
                              />
                              <span className="text-sm font-medium">{type}</span>
                              {formData.windowTypes.includes(type) && (
                                <SafeIcon icon={FiCheck} className="text-blue-600 ml-auto" />
                              )}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-4">How many windows?</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {['1–2', '3–5', '6–9', '10+'].map((count) => (
                            <label
                              key={count}
                              className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                formData.windowCount === count
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="windowCount"
                                value={count}
                                checked={formData.windowCount === count}
                                onChange={(e) => handleInputChange('windowCount', e.target.value)}
                                className="sr-only"
                              />
                              <span className="font-semibold">{count}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Doors Questions */}
                  {formData.products.includes('Doors') && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-bold text-gray-800">Doors</h3>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-4">What type of doors?</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {['Front Door', 'Back Door', 'French Doors', 'Patio Sliding Doors', 'Bi-Fold Doors', 'Composite Door', 'uPVC Door', 'Not Sure'].map((type) => (
                            <label
                              key={type}
                              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                                formData.doorTypes.includes(type)
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.doorTypes.includes(type)}
                                onChange={() => handleCheckboxChange('doorTypes', type)}
                                className="sr-only"
                              />
                              <span className="text-sm font-medium">{type}</span>
                              {formData.doorTypes.includes(type) && (
                                <SafeIcon icon={FiCheck} className="text-blue-600 ml-auto" />
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Conservatory Questions */}
                  {formData.products.includes('Conservatory') && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-bold text-gray-800">Conservatory</h3>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-4">What do you need for your conservatory?</h4>
                        <div className="space-y-2">
                          {['New Build', 'Roof Replacement', 'Window/Door Replacement', 'Repair or Upgrade', 'Not Sure'].map((need) => (
                            <label
                              key={need}
                              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                formData.conservatoryNeed === need
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="conservatoryNeed"
                                value={need}
                                checked={formData.conservatoryNeed === need}
                                onChange={(e) => handleInputChange('conservatoryNeed', e.target.value)}
                                className="sr-only"
                              />
                              <span className="font-semibold">{need}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-4">What style of conservatory do you prefer?</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {['Lean-To', 'Victorian', 'Edwardian', 'Gable', 'Orangery', 'Not Sure'].map((style) => (
                            <label
                              key={style}
                              className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                formData.conservatoryStyle === style
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="conservatoryStyle"
                                value={style}
                                checked={formData.conservatoryStyle === style}
                                onChange={(e) => handleInputChange('conservatoryStyle', e.target.value)}
                                className="sr-only"
                              />
                              <span className="font-semibold text-center">{style}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 3: Project Information */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <h2 className="text-2xl font-bold text-gray-800">Project Information</h2>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">What kind of property is it?</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {['House', 'Flat', 'Bungalow', 'Commercial', 'Other'].map((type) => (
                        <label
                          key={type}
                          className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.propertyType === type
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="propertyType"
                            value={type}
                            checked={formData.propertyType === type}
                            onChange={(e) => handleInputChange('propertyType', e.target.value)}
                            className="sr-only"
                          />
                          <span className="font-semibold">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">When do you plan to start the work?</h3>
                    <div className="space-y-2">
                      {['ASAP', 'In 1–3 Months', 'In 3–6 Months', 'Just Researching'].map((timeline) => (
                        <label
                          key={timeline}
                          className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.timeline === timeline
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="timeline"
                            value={timeline}
                            checked={formData.timeline === timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="sr-only"
                          />
                          <SafeIcon icon={FiClock} className="text-blue-600 mr-3" />
                          <span className="font-semibold">{timeline}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Optional Preferences */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800">Optional Preferences</h2>
                    <button
                      type="button"
                      onClick={() => setShowPreferences(!showPreferences)}
                      className="flex items-center text-blue-600 hover:text-blue-700"
                    >
                      <span className="mr-2">{showPreferences ? 'Hide' : 'Show'} Options</span>
                      <SafeIcon icon={showPreferences ? FiChevronUp : FiChevronDown} />
                    </button>
                  </div>

                  <AnimatePresence>
                    {showPreferences && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-8"
                      >
                        {/* Material Preference - Removed "Wood" from the list */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-700 mb-4">Do you have a preferred material?</h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {['uPVC', 'Timber', 'Aluminium', 'Composite', 'Not Sure'].map((material) => (
                              <label
                                key={material}
                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                                  formData.materials.includes(material)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.materials.includes(material)}
                                  onChange={() => handleCheckboxChange('materials', material)}
                                  className="sr-only"
                                />
                                <span className="text-sm font-medium">{material}</span>
                                {formData.materials.includes(material) && (
                                  <SafeIcon icon={FiCheck} className="text-blue-600 ml-auto" />
                                )}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Glazing Type */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-700 mb-4">What type of glazing would you like?</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {['Double Glazing', 'Triple Glazing', 'Secondary Glazing', 'Not Sure'].map((glazing) => (
                              <label
                                key={glazing}
                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                                  formData.glazingType.includes(glazing)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.glazingType.includes(glazing)}
                                  onChange={() => handleCheckboxChange('glazingType', glazing)}
                                  className="sr-only"
                                />
                                <span className="text-sm font-medium">{glazing}</span>
                                {formData.glazingType.includes(glazing) && (
                                  <SafeIcon icon={FiCheck} className="text-blue-600 ml-auto" />
                                )}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Energy Efficiency */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-700 mb-4">Energy efficiency preference?</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {['Standard', 'A-Rated / Energy Efficient', 'Not Sure'].map((efficiency) => (
                              <label
                                key={efficiency}
                                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  formData.energyEfficiency === efficiency
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="energyEfficiency"
                                  value={efficiency}
                                  checked={formData.energyEfficiency === efficiency}
                                  onChange={(e) => handleInputChange('energyEfficiency', e.target.value)}
                                  className="sr-only"
                                />
                                <span className="font-semibold">{efficiency}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Color Preference */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-700 mb-4">Preferred colour or finish?</h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {['White', 'Grey', 'Black', 'Wood Effect', 'Other / Custom', 'Not Sure'].map((color) => (
                              <label
                                key={color}
                                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  formData.color === color
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="color"
                                  value={color}
                                  checked={formData.color === color}
                                  onChange={(e) => handleInputChange('color', e.target.value)}
                                  className="sr-only"
                                />
                                <span className="font-semibold">{color}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Budget */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-700 mb-4">What's your estimated budget?</h3>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {['Under £2,000', '£2,000–£5,000', '£5,000–£10,000', '£10,000+', 'Not Sure'].map((budget) => (
                              <label
                                key={budget}
                                className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  formData.budget === budget
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="budget"
                                  value={budget}
                                  checked={formData.budget === budget}
                                  onChange={(e) => handleInputChange('budget', e.target.value)}
                                  className="sr-only"
                                />
                                <span className="font-semibold text-center">{budget}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {!showPreferences && (
                    <div className="text-center py-8">
                      <p className="text-gray-600 mb-4">Skip preferences and continue to contact information</p>
                      <button
                        type="button"
                        onClick={nextStep}
                        className="text-blue-600 hover:text-blue-700 font-semibold"
                      >
                        Skip to Contact Details →
                      </button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 5: Contact Information */}
              {currentStep === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Information</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <SafeIcon icon={FiMapPin} className="inline mr-1" />
                        Postcode *
                      </label>
                      <input
                        type="text"
                        value={formData.postcode}
                        onChange={(e) => handleInputChange('postcode', e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="e.g. SW1A 1AA"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <SafeIcon icon={FiPhone} className="inline mr-1" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <SafeIcon icon={FiMail} className="inline mr-1" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="Enter your email address"
                        required
                      />
                    </div>
                  </div>

                  {/* Message Section */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <SafeIcon icon={FiMessageSquare} className="inline mr-1" />
                      Additional Message (Optional)
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors resize-none"
                      rows="4"
                      placeholder="Tell us more about your project, specific requirements, or any questions you have..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Share any specific requirements, measurements, or questions to help us provide better quotes.
                    </p>
                  </div>

                  <div className="mt-8">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => handleInputChange('consent', e.target.checked)}
                        className="mt-1 w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
                        required
                      />
                      <span className="text-sm text-gray-700">
                        <SafeIcon icon={FiShield} className="inline mr-1 text-green-600" />
                        I agree to be contacted by up to 3 local installers regarding my quote request. *
                      </span>
                    </label>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 mt-6">
                    <p className="text-sm text-blue-800 text-center">
                      ✔ 100% Free • No Obligation • Local Installers • Instant Delivery
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-green-800">
                      <strong>🚀 How it works:</strong> When you submit this form, we'll send your quote request instantly to our system via webhook. Our team will receive it immediately and contact you within 24 hours.
                    </p>
                    <p className="text-xs mt-1 text-green-700">
                      We'll contact you within 24 hours with up to 3 local installer quotes.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
              )}

              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!validateStep(currentStep)}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all ml-auto ${
                    validateStep(currentStep)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!validateStep(5) || isSubmitting}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all ml-auto ${
                    validateStep(5) && !isSubmitting
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending to Webhook...
                    </span>
                  ) : (
                    '🚀 Send Quote Request Instantly'
                  )}
                </button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default QuoteForm;